import _ from 'lodash';
import clsx from 'clsx';
import Style from './conditionCreator.module.scss';
import ExpressionBuilder from './expression/expressionBuilder';
import { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { useEditor, useNode } from '@craftjs/core';
import { Controller, FieldValues, useForm, UseFormReturn } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import {
  ExpressionArrayType,
  ExpressionNodeType,
  LogicalOperators,
  LogicalOperatorType,
  NodeOperatorType,
} from './expression/types';
import { AppDropDownWithSuggestionInput } from 'src/components/uikit/inputs/dropdown/appDropDownWithSuggestion';
import { DropdownOption } from 'src/components/uikit/inputs/dropdown/appDropdownInput';
import { MetricActions, MetricCategories } from 'src/application/analitics/umami/TrackEventParams';
import { useUmamiTracker } from 'src/application/analitics/umami/useUmamiTracker';
import { ConditionalProps, FormFieldProps, isFieldWithOptions } from '../../../types/BaseControlTypes';
import { IControlsResolver } from '../../../types/IControlsResolver';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { AppButton } from 'uikit/buttons';
import { DialogModal } from 'src/components/dialogModal/dialogModal.component';
import { ReactComponent as WarningIcon } from 'assets/img/generalBuilder/warning_16.svg';
import { ReactComponent as DeleteIcon } from 'assets/img/generalBuilder/delete_24.svg';
import { AppInputError } from 'uikit/wrappers';
import { ValidationFormRules } from 'src/helpers/validation-helpers';
import { AppTextInput } from 'uikit/inputs';
import { SettingsMenu } from '../uiKit/menu/settingsMenu';
import { getNodeByDataKeyFunc, useGetAvailableNodes } from './helper';
import { useStudyNoteSelectors } from 'src/features/generalForms/studyNoteForms/selectors';
import { Chip } from '../uiKit/chip/chip';
import { DropDownMenuOption } from 'uikit/dropDownMenu/dropDownMenu';
import { getConditionalInvalidValuesError } from './conditionalInvalidValuesError';
import { getTimeInputOptions } from 'src/features/generalForms/uikit/fields/time/timePickerOptions';
import { hookFormRules } from 'src/features/generalForms/fillingModal/react-hook-form-helper';
import { TimePicker } from 'src/features/generalForms/uikit/fields/time/timePicker';
/**
 * Use to manage of "visible" state for HookForm components only
 * P.S. Used when form is filling.
 */
export const useHookFormConditional = (form: UseFormReturn<FieldValues>) => {
  const { query } = useEditor();
  const { visibleIf, enableIf, id } = useNode((node) => ({
    visibleIf: (node.data.props as ConditionalProps).visibleIf,
    enableIf: (node.data.props as ConditionalProps).enableIf,
  }));

  const dataKeyPrefix = useMemo(
    () =>
      query
        .node(id)
        .ancestors(true)
        .map((nodeId) => query.node(nodeId).get().data.props.dataKey)
        .filter(Boolean)
        .reverse()
        .join('.'),
    [id, query],
  );

  const result = useMemo(
    () => getConditionalState(visibleIf, enableIf, form, dataKeyPrefix),
    [enableIf, form, dataKeyPrefix, visibleIf],
  );

  return result;
};

const getConditionalState = (
  visibleIf: string | undefined,
  enableIf: string | undefined,
  form: UseFormReturn<FieldValues>,
  dataKeyPrefix?: string,
) => {
  // TODO: think about rerenders
  const visible =
    (!!visibleIf &&
      ExpressionBuilder.fromString(visibleIf).evaluate((dataKey) =>
        form?.watch(!!dataKeyPrefix ? `${dataKeyPrefix}.${dataKey}` : dataKey),
      )) ||
    !visibleIf;

  const enable =
    (!!enableIf &&
      ExpressionBuilder.fromString(enableIf).evaluate((dataKey) =>
        form?.watch(!!dataKeyPrefix ? `${dataKeyPrefix}.${dataKey}` : dataKey),
      )) ||
    !enableIf;

  return { visible, enable };
};

/**
 * Use to manage of "visible" prop for answer overview components only.
 * P.S. Used on answers preview.
 */
// export const useAnswerRowConditional = (): { visible: boolean } => {
//   const answers = useContextSelector(BuilderContext, (x) => x.answers) || {};
//   const { visibleIf } = useNode((node) => ({
//     visibleIf: (node.data.props as ConditionalProps).visibleIf,
//   }));

//   const visible =
//     (!!visibleIf && ExpressionBuilder.fromString(visibleIf).evaluate((dataKey) => answers[dataKey])) || !visibleIf;

//   return { visible };
// };

type OperatorResolverType = {
  [K in keyof IControlsResolver]: NodeOperatorType[] | undefined;
};

export const OperatorResolver: OperatorResolverType = {
  RootContainer: undefined,
  FormContainer: undefined,
  FormStepContainer: undefined,
  FormText: undefined,
  FormFormula: undefined,
  FormImage: undefined,
  FormVideo: undefined,
  FormTabContainer: undefined,
  FormDividingLine: undefined,
  RepeatableSection: undefined,

  RepeatableContainer: ['empty', 'notempty'],
  FormTextInput: ['empty', 'notempty'],
  FormMultipleTextInput: ['empty', 'notempty'],
  FormNumberInput: ['empty', 'notempty', 'anyof', '===', '!==', '>', '<', '>=', '<='],
  FormSliderInput: ['empty', 'notempty', 'anyof', '===', '!==', '>', '<', '>=', '<='],
  FormDateInput: ['empty', 'notempty', '===', '!==', '>', '<', '>=', '<='],
  FormDateRangeInput: ['empty', 'notempty'],
  FormTimeInput: ['empty', 'notempty', 'anyof', '===', '!==', '>', '<', '>=', '<='],
  FormRadioGroupInput: ['empty', 'notempty', 'anyof', '===', '!=='],
  FormDropdownInput: ['empty', 'notempty', 'anyof', '===', '!=='],
  FormMultipleDropdownInput: ['empty', 'notempty', 'anyof', 'contains'],
  FormCheckboxGroupInput: ['empty', 'notempty', 'anyof', 'contains'],
  FormTagInput: ['empty', 'notempty'],
  FormFileInput: ['empty', 'notempty'],
};

export type ConditionalModalType = 'visibleIf' | 'enableIf';

/**
 * Modal to create conditional string
 */
export const useConditionalCreator = (type: ConditionalModalType) => {
  const { t: commonT } = useTranslation();
  const { t } = useScopedTranslation('GeneralFormBuilder.Panels.Logic');
  const { trackEvent } = useUmamiTracker();
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string>();
  const promiseRef = useRef<(test: string | null) => void>();
  const [expression, setExpression] = useState<ExpressionArrayType>();
  const [editorType, setEditorType] = useState<'create' | 'edit'>('create');
  const controlsResolver = useStudyNoteSelectors();
  const { props, displayName } = useNode((node) => ({
    props: node.data.props as FormFieldProps,
    // @ts-ignore
    displayName: controlsResolver[node.data.name]?.name,
  }));
  const { getNodeByDataKey } = useEditor((state) => ({
    getNodeByDataKey: getNodeByDataKeyFunc(state),
  }));

  const availableNodes = useGetAvailableNodes();

  const form = useForm();
  const handleSubmit = form.handleSubmit(() => {
    if (!expression) {
      onModalClose(null);
      return;
    }

    try {
      onModalClose(ExpressionBuilder.fromArray(expression).toString());
    } catch (err: any) {
      setError(err.message);
    }
  });

  useEffect(() => {
    if (!expression?.nodes) return;
    for (const node of expression.nodes) {
      form.setValue(node.dataKey + node.operator, node.value);
    }
  }, [expression?.nodes, form]);

  const onModalClose = useCallback(
    (returnValue: string | null) => {
      setIsOpen(false);
      setError(undefined);
      form.reset();
      promiseRef?.current?.(returnValue);
    },
    [form],
  );

  const addNodeHandler = useCallback(
    (operator: LogicalOperatorType) => {
      const newNode: ExpressionNodeType = {
        dataKey: availableNodes[0].data.props.dataKey,
        value: null,
        operator: 'notempty',
      };

      setExpression((curr) => {
        if (!curr) return undefined;

        return {
          nodes: [...curr.nodes, newNode],
          operators: [...curr.operators, operator],
        };
      });
    },
    [availableNodes],
  );

  const removeNodeHandler = useCallback(
    (index: number) => () => {
      if (!expression?.operators.length) return;
      setExpression((curr) => {
        if (!curr) return undefined;

        curr.nodes.splice(index, 1);
        curr.operators.splice(index - 1, 1);
        return {
          nodes: [...curr.nodes],
          operators: [...curr.operators],
        };
      });
    },
    [expression?.operators.length],
  );

  const changeDataKeyHandler = useCallback((newDataKey: string, index: number) => {
    setExpression((curr) => {
      if (!curr) return undefined;

      curr.nodes[index].dataKey = newDataKey;
      curr.nodes[index].value = null;
      curr.nodes[index].operator = 'notempty';

      return {
        nodes: [...curr.nodes],
        operators: [...curr.operators],
      };
    });

    setError(undefined);
  }, []);

  const changeValueHandler = useCallback((newValue: any, index: number) => {
    setExpression((curr) => {
      if (!curr) return undefined;

      curr.nodes[index].value = newValue;
      return {
        nodes: [...curr.nodes],
        operators: [...curr.operators],
      };
    });
    setError(undefined);
  }, []);

  const getInvalidOptionError = useCallback(
    (node: ExpressionNodeType) => {
      return getConditionalInvalidValuesError(
        node,
        getNodeByDataKey,
        (values) => `${t('Errors.MissingValues', { values })}`,
      );
    },
    [t, getNodeByDataKey],
  );

  const renderComparisonOperator = useCallback(
    (dataKey: string, operator: NodeOperatorType, onChange: (newOperator: NodeOperatorType) => void) => {
      const resolvedName = getNodeByDataKey(dataKey)?.data.name;
      if (!resolvedName) return <></>;

      const logicalOperators = OperatorResolver[resolvedName];
      if (!logicalOperators || !logicalOperators.length) return <></>;

      const comparisonOperatorOptions: DropDownMenuOption[] | undefined = logicalOperators.map((x) => ({
        key: x,
        text: t(`ComparisonOperator.${x}`),
        variant: 'default',
      }));

      return (
        <SettingsMenu
          menuItems={comparisonOperatorOptions}
          defaultItem={_.first(comparisonOperatorOptions)!}
          onSelect={(x) => onChange(x.key as NodeOperatorType)}
          className={Style.menu}
          customComponent={
            <Chip
              colorSchema={'green'}
              text={t(`ComparisonOperator.${operator}`)}
              className={Style.chip}
              // NOTE: For interactive styles
              onClick={() => {}}
            />
          }
        />
      );
    },
    [getNodeByDataKey, t],
  );

  const renderLogicOperator = useCallback(
    (initialOperator: LogicalOperatorType | undefined, onChange?: (newOperator: LogicalOperatorType) => void) => {
      const logicalOperatorOptions: DropDownMenuOption[] = LogicalOperators.map((x) => ({
        key: x,
        text: t(`LogicalOperator.${x}`),
        variant: 'default',
      }));

      return (
        <SettingsMenu
          menuItems={logicalOperatorOptions}
          defaultItem={_.first(logicalOperatorOptions)!}
          onSelect={(x) => onChange?.(x.key as LogicalOperatorType)}
          className={Style.menu}
          customComponent={
            <Chip
              colorSchema={'yellow'}
              text={initialOperator ? t(`LogicalOperator.${initialOperator}`) : t('AddCondition')}
              className={clsx(Style.chip, {
                [Style.notSelected]: !initialOperator,
              })}
              // NOTE: For interactive styles
              onClick={() => {}}
            />
          }
        />
      );
    },
    [t],
  );

  const inputFieldResolver = useCallback(
    (node: ExpressionNodeType, index: number) => {
      const prop = getNodeByDataKey(node.dataKey)?.data.props;
      const withOptions = isFieldWithOptions(prop);
      const resolvedName = getNodeByDataKey(node.dataKey)?.data.name;
      const isDateInput = resolvedName === 'FormDateInput';
      const isTimeInput = resolvedName === 'FormTimeInput';
      const timeOptions = isTimeInput ? getTimeInputOptions(prop?.timeIntervalInMinutes) : undefined;
      const invalidOptionError = getInvalidOptionError(node);

      const optionFieldRules = {
        ...hookFormRules({
          required: true,
        }),
        validate: () => invalidOptionError ?? undefined,
      };

      if (node.operator === 'empty' || node.operator === 'notempty') {
        return <></>;
      }

      if (isTimeInput && node.operator === 'anyof') {
        return (
          <Controller
            control={form.control}
            name={node.dataKey + node.operator}
            rules={optionFieldRules}
            render={({ field: { onChange }, fieldState: { error: fieldError } }) => (
              <AppInputError hideBorder={true} errors={fieldError?.message || invalidOptionError}>
                <AppDropDownWithSuggestionInput
                  isMultiple={true}
                  isRequired={true}
                  options={timeOptions ?? []}
                  value={timeOptions?.filter((option) =>
                    Array.isArray(node.value) ? node.value.includes(option.key) : [node.value].includes(option.key),
                  )}
                  onChange={(value) => {
                    const newValue = value?.map((item) => item.key);

                    onChange(newValue);
                    changeValueHandler(newValue, index);
                  }}
                />
              </AppInputError>
            )}
          />
        );
      }

      if (withOptions && node.operator === 'anyof') {
        const options: DropdownOption[] = prop?.options.map((option) => {
          return {
            key: option.key,
            text: option.value,
          };
        });

        return (
          <Controller
            control={form.control}
            name={node.dataKey + node.operator}
            rules={optionFieldRules}
            render={({ field: { onChange }, fieldState: { error: fieldError } }) => (
              <AppInputError hideBorder={true} errors={fieldError?.message || invalidOptionError}>
                <AppDropDownWithSuggestionInput
                  isMultiple={true}
                  isRequired={true}
                  options={options}
                  value={options.filter((o) => node.value?.includes(o.key))}
                  onChange={(value) => {
                    onChange(value?.map((v) => v.key));
                    changeValueHandler(
                      value?.map((v) => v.key),
                      index,
                    );
                  }}
                />
              </AppInputError>
            )}
          />
        );
      }

      if (isTimeInput) {
        return (
          <Controller
            control={form.control}
            name={node.dataKey + node.operator}
            rules={optionFieldRules}
            render={({ field: { onChange }, fieldState: { error: fieldError } }) => (
              <AppInputError hideBorder={true} errors={fieldError?.message || invalidOptionError}>
                <TimePicker
                  isRequired={true}
                  timeInMills={node.value ?? null}
                  timeEntriesIntervalInMinutes={prop?.timeIntervalInMinutes}
                  onTimeChanged={(newValue) => {
                    const resolvedValue = newValue ?? null;

                    onChange(resolvedValue);
                    changeValueHandler(resolvedValue, index);
                  }}
                />
              </AppInputError>
            )}
          />
        );
      }

      if (isDateInput) {
        return (
          <Controller
            control={form.control}
            name={node.dataKey + node.operator}
            rules={hookFormRules({ required: true })}
            render={({ field: { onChange }, fieldState: { error: fieldError } }) => (
              <AppInputError hideBorder={true} errors={fieldError?.message}>
                <AppTextInput
                  value={node.value || ''}
                  type={'date'}
                  onChange={(e) => {
                    onChange(e.target.value || null);
                    changeValueHandler(e.target.value || null, index);
                  }}
                />
              </AppInputError>
            )}
          />
        );
      }

      if (withOptions) {
        const options: DropdownOption[] = prop?.options.map((option) => {
          return {
            key: option.key,
            text: option.value,
          };
        });

        return (
          <Controller
            control={form.control}
            name={node.dataKey + node.operator}
            rules={optionFieldRules}
            render={({ field: { onChange }, fieldState: { error: fieldError } }) => (
              <AppInputError hideBorder={true} errors={fieldError?.message || invalidOptionError}>
                <AppDropDownWithSuggestionInput
                  options={options}
                  isRequired={true}
                  value={options.find((o) => o.key === node.value)}
                  onChange={(o) => {
                    onChange(o?.key || null);
                    changeValueHandler(o?.key || null, index);
                  }}
                />
              </AppInputError>
            )}
          />
        );
      }

      return (
        <Controller
          control={form.control}
          name={node.dataKey + node.operator}
          rules={{ ...ValidationFormRules().requiredRule }}
          render={({ field: { onChange }, fieldState: { error: err } }) => (
            <AppInputError hideBorder={true} errors={err?.message}>
              <AppTextInput
                value={node.value || ''}
                type={'number'}
                onChange={(e) => {
                  onChange(e.target.value || null);
                  changeValueHandler(e.target.value || null, index);
                }}
              />
            </AppInputError>
          )}
        />
      );
    },
    [changeValueHandler, form.control, getInvalidOptionError, getNodeByDataKey],
  );

  const renderExpression = useCallback(() => {
    if (!expression) {
      return <></>;
    }

    const comparisonOperatorChangeHandler = (index: number) => (newOperator: NodeOperatorType) => {
      setExpression((curr) => {
        if (!curr) return undefined;

        curr.nodes[index].operator = newOperator;
        return {
          nodes: [...curr.nodes],
          operators: [...curr.operators],
        };
      });
    };

    const logicalOperatorChangeHandler = (index: number) => (newOperator: LogicalOperatorType) => {
      setExpression((curr) => {
        if (!curr) return undefined;

        curr.operators[index] = newOperator;
        return {
          nodes: [...curr.nodes],
          operators: [...curr.operators],
        };
      });
    };

    const options: DropdownOption[] = availableNodes.map((node) => {
      return {
        key: node.data.props.dataKey,
        text:
          node.data.props.title ??
          commonT(`GeneralFormBuilder.Panels.Controls.${node.data.displayName}`, `DataKey: ${node.data.props.dataKey}`),
      };
    });

    return expression.nodes.map((node, i) => (
      <div key={i} className={Style.nodeContainer}>
        {i > 0 && renderLogicOperator(expression.operators[i - 1], logicalOperatorChangeHandler(i - 1))}
        <div className={Style.header}>
          <AppDropDownWithSuggestionInput
            options={_.sortBy(options, (x) => x.text?.toLowerCase())}
            isRequired={true}
            suggestion={true}
            value={options.find((x) => x.key === node.dataKey)}
            onChange={(option) => changeDataKeyHandler(option!.key.toString(), i)}
            inputClassName={Style.nodeDropdown}
            hideCaret={true}
          />
          {renderComparisonOperator(node.dataKey, node.operator, comparisonOperatorChangeHandler(i))}
          {!!expression.operators.length && (
            <AppButton
              variant={'icon-link'}
              colorSchema={'basic'}
              Icon={DeleteIcon}
              className={Style.delete}
              onClick={removeNodeHandler(i)}
            />
          )}
        </div>
        {inputFieldResolver(node, i)}
      </div>
    ));
  }, [
    expression,
    availableNodes,
    commonT,
    renderLogicOperator,
    renderComparisonOperator,
    removeNodeHandler,
    inputFieldResolver,
    changeDataKeyHandler,
  ]);

  const dataKeyTooltip = useMemo(() => {
    if (editorType === 'edit') return null;

    return (
      <div className={Style.dataKeyTooltip}>
        <WarningIcon />
        <div>{t('DataKeyTooltip')}</div>
      </div>
    );
  }, [editorType, t]);

  return {
    start: async (initialExpression?: string) => {
      setEditorType(initialExpression ? 'edit' : 'create');

      const exp = initialExpression
        ? ExpressionBuilder.fromString(initialExpression)
        : ExpressionBuilder.fromNode({
            dataKey: availableNodes[0].data.props.dataKey,
            operator: 'notempty',
            value: null,
          });
      setExpression(exp.toArray());

      setIsOpen(true);

      trackEvent({
        action: MetricActions.ButtonPressed,
        category: MetricCategories.Survey,
        name: 'Opened logic modal',
      });

      return new Promise<string | null>((resolve) => {
        promiseRef.current = resolve;
      });
    },
    modal: (
      <DialogModal
        onHide={() => onModalClose(null)}
        visible={isOpen}
        title={t(`ModalTitle.${editorType}`)}
        bodyClassName={Style.dialog}
        footer={{
          errors: error,
          leftButton: {
            colorSchema: 'secondary',
            text: commonT('Common_Cancel'),
            onClick: () => onModalClose(null),
          },
          rightButton: {
            isLoading: form.formState.isSubmitting,
            colorSchema: 'primary',
            text: commonT('Common_Save'),
            onClick: handleSubmit,
          },
        }}
      >
        <div className={Style.container}>
          {dataKeyTooltip}
          <div className={Style.titleContainer}>
            <Trans
              i18nKey={`GeneralFormBuilder.Panels.Logic.ModalHeader.${type}`}
              values={{ title: props.title || displayName }}
            >
              <span className={clsx(Style.title, 'truncate')} />
            </Trans>
          </div>
          {renderExpression()}
          <div className={Style.addButton}>{renderLogicOperator(undefined, addNodeHandler)}</div>
        </div>
      </DialogModal>
    ),
  };
};
